import { CATEGORY_COLORS } from "./vitalCategories";

/**
 * OOR report views (the backend `report_view` values) and the display
 * metadata for the Extended Threshold Report (SCRUM-790, Figma 7119-798 /
 * 7089-1902).
 */

export const REPORT_VIEWS = {
  CATEGORY: "category",
  LIST: "list",
  EXTENDED_THRESHOLD: "extended_threshold",
};

export const REPORT_VIEW_LABELS = {
  [REPORT_VIEWS.CATEGORY]: "Category Report",
  [REPORT_VIEWS.LIST]: "List Report",
  [REPORT_VIEWS.EXTENDED_THRESHOLD]: "Extended Threshold Report",
};

export const reportViewLabel = (view) =>
  REPORT_VIEW_LABELS[view] || REPORT_VIEW_LABELS[REPORT_VIEWS.CATEGORY];

/**
 * Report numbers exist only on SUBMITTED Extended Threshold reports. Prefer
 * the backend's zero-padded display ("001"); pad the raw number otherwise.
 */
export const formatReportNumber = (number, display) => {
  if (display) return String(display);
  if (number == null || number === "") return null;
  return String(number).padStart(3, "0");
};

/**
 * Systolic BP categories, keyed by the backend `category` enum. Icon shapes
 * and colors are from the Figma polygons: hypotension = blue outline
 * triangle pointing down, stage 2 = filled red triangle, hypertensive care =
 * dark red diamond. The label/range pairs are the Figma fallback when the
 * snapshot carries no `threshold_key`.
 */
export const ET_CATEGORIES = {
  hypotension: {
    label: "Hypotensive",
    range: "<90",
    shape: "triangleDown",
    color: CATEGORY_COLORS.blue,
  },
  stage_2: {
    label: "Stage 2 Hypertension",
    range: "141–159",
    shape: "triangleUp",
    color: CATEGORY_COLORS.red,
  },
  hypertensive_care: {
    label: "Hypertensive Care",
    range: ">=160",
    shape: "diamond",
    color: CATEGORY_COLORS.darkRed,
  },
};

export const ET_CATEGORY_ORDER = ["hypotension", "stage_2", "hypertensive_care"];

/**
 * Consecutive-report row highlight (the API's `highlight`; null = first
 * appearance, no fill). `row` is the table/PDF row fill, `swatch` the legend
 * chip fill, both from the Figma (7119-798); keys are the API's values.
 */
export const ET_HIGHLIGHTS = {
  yellow: {
    row: "#fffedb",
    swatch: "#fffedb",
    description: "2 consecutive submitted reports",
  },
  orange: {
    row: "#ffd182",
    swatch: "#ffda9b",
    description: "3 consecutive submitted reports",
  },
  red: {
    row: "#ffb3b3",
    swatch: "#ffb3b3",
    description: "4+ consecutive submitted reports",
  },
};

export const ET_HIGHLIGHT_ORDER = ["yellow", "orange", "red"];

/**
 * Threshold key entries to render: the snapshot's `threshold_key` as-is when
 * present (backend owns the wording), else the Figma defaults.
 */
export const resolveThresholdKey = (thresholdKey) => {
  if (Array.isArray(thresholdKey) && thresholdKey.length) {
    return thresholdKey.map((k) => ({
      category: k.category,
      label: k.label || ET_CATEGORIES[k.category]?.label || k.category,
      description: k.description || ET_CATEGORIES[k.category]?.range || "",
    }));
  }
  return ET_CATEGORY_ORDER.map((category) => ({
    category,
    label: ET_CATEGORIES[category].label,
    description: ET_CATEGORIES[category].range,
  }));
};

/** Highlight key entries: the snapshot's `highlight_key`, else the Figma defaults. */
export const resolveHighlightKey = (highlightKey) => {
  if (Array.isArray(highlightKey) && highlightKey.length) {
    return highlightKey
      .filter((k) => ET_HIGHLIGHTS[k.highlight])
      .map((k) => ({
        highlight: k.highlight,
        description:
          k.description || k.label || ET_HIGHLIGHTS[k.highlight].description,
      }));
  }
  return ET_HIGHLIGHT_ORDER.map((highlight) => ({
    highlight,
    description: ET_HIGHLIGHTS[highlight].description,
  }));
};
